import { LoadingOverlay, Menu, Modal, ScrollArea, Select } from '@mantine/core'
import { Store } from 'pullstate'
import { Draft } from 'immer'
import { dayjsRange, monthYmds, Ymd, ymdDayjs, ymdFromISO } from '../utils/ymd'
import { useCallback, useEffect, useMemo } from 'react'
import classes from './Calendar.module.css'
import { IconMinus, IconPlus } from '@tabler/icons-react'
import { useDisclosure, useMediaQuery, useViewportSize } from '@mantine/hooks'
import { Berth } from '../models/marina'
import axios from 'axios'
import { useQueryClient } from '@tanstack/react-query'
import { EditBerthForm } from './EditBerthForm'

interface Props {
  period: Date
  marinaId: number
  berths: Berth[]
  onAddBerths: () => void
}

interface CalendarState {
  berthDays: Map<number, Set<Ymd>>
  visibleBerths: number[]
  selectedBerthId: number | null
  selectStart: Ymd | null
  selectOver: Ymd | null
  selectEnd: Ymd | null
  selectMenu: boolean
  saving: boolean
}

function initCalendarState(berths: Berth[], visible?: number[]): CalendarState {
  let berthDays = new Map()
  berths.forEach((berth) => {
    let days = new Set(berth.available_days.map(({ date }) => ymdFromISO(date)))
    berthDays.set(berth.berth_id, days)
  })
  return {
    berthDays,
    visibleBerths: visible || [],
    selectedBerthId: null,
    selectStart: null,
    selectOver: null,
    selectEnd: null,
    selectMenu: false,
    saving: false,
  }
}

export const CalendarStore = new Store(initCalendarState([]))

export function Calendar(props: Props) {
  const period = props.period
  const days = useMemo(() => monthYmds(period), [period])
  const [saving, visibleBerths] = CalendarStore.useState<
    [boolean, Set<number>]
  >((s) => [s.saving, new Set(s.visibleBerths)])
  const { width } = useViewportSize()
  const dayWidth = Math.min(36, Math.max(30, Math.floor((width - 200) / 31)))
  const berths = props.berths

  useSelectEndedListener()
  useEffect(() => {
    CalendarStore.update((s) => {
      let visibleBerths = s.visibleBerths
      if (berths.length < 5 && s.visibleBerths.length < 5) {
        visibleBerths = Array.from(
          new Set([...s.visibleBerths, ...berths.map((b) => b.berth_id)])
        )
      } else if (
        !berths.some((b) => b.available_days.length > 0) &&
        s.visibleBerths.length === 0
      ) {
        visibleBerths = berths.slice(0, 5).map((b) => b.berth_id)
      }
      return initCalendarState(berths, visibleBerths)
    })
  }, [period, berths])

  return (
    <div className={classes.container}>
      <LoadingOverlay visible={saving} />
      <ScrollArea w={Math.min(width, 1280)}>
        <div className={classes.table}>
          <HeadRow width={dayWidth} days={days} />
          {berths
            .filter(
              (berth, i) =>
                berth.available_days.length > 0 ||
                visibleBerths.has(berth.berth_id)
            )
            .map((berth) => (
              <Row
                width={dayWidth}
                key={berth.berth_id}
                berth={berth}
                days={days}
              />
            ))}
          <FootRow berths={berths} />
        </div>
      </ScrollArea>
    </div>
  )
}

function useSelectEndedListener() {
  useEffect(() => {
    let up = () => {
      CalendarStore.update(selectEnded(null))
    }
    document.body.addEventListener('mouseup', up)
    return () => {
      document.body.removeEventListener('mouseup', up)
    }
  }, [])
}

const selectEnded = (ymd: Ymd | null) => (s: Draft<CalendarState>) => {
  //console.log('END', s.selectStart, s.selectOver, ymd)
  if (s.selectMenu) return
  s.selectMenu = true
  s.selectOver = ymd
  s.selectEnd = ymd
}

const selectCanceled = () => (s: Draft<CalendarState>) => {
  s.selectMenu = false
  s.selectStart = null
  s.selectOver = null
  s.saving = false
}

const selectStarted =
  (ymd: Ymd, berthId: number) => (s: Draft<CalendarState>) => {
    s.selectMenu = false
    s.selectStart = ymd
    s.selectOver = ymd
    s.selectedBerthId = berthId
  }

const selectOver = (ymd: Ymd) => (s: Draft<CalendarState>) => {
  if (s.selectStart == null || s.selectMenu) return
  s.selectOver = ymd
}

function useDayClass(berthId: number, ymd: number): string {
  return CalendarStore.useState((s) => {
    let cls = ['']

    if (s.berthDays.get(berthId)?.has(ymd)) {
      cls.push(classes.available)
    }

    if (
      s.selectedBerthId !== berthId ||
      s.selectStart == null ||
      s.selectOver == null
    ) {
      // +- 1 works only if all ymd are in the same month
      if (s.berthDays.get(berthId)?.has(ymd)) {
        if (!s.berthDays.get(berthId)?.has(ymd - 1))
          cls.push(classes.selectedStart)
        if (!s.berthDays.get(berthId)?.has(ymd + 1))
          cls.push(classes.selectedEnd)
      }
      return cls.join(' ')
    }

    if (s.selectStart <= s.selectOver) {
      if (s.selectStart <= ymd && ymd <= s.selectOver)
        cls.push(classes.selected)
      if (s.selectStart === ymd) cls.push(classes.selectedStart)
      if (s.selectOver === ymd) cls.push(classes.selectedEnd)
    } else {
      if (s.selectOver <= ymd && ymd <= s.selectStart)
        cls.push(classes.selected)
      if (s.selectStart === ymd) cls.push(classes.selectedEnd)
      if (s.selectOver === ymd) cls.push(classes.selectedStart)
    }
    return cls.join(' ')
  })
}

interface HeadRowProps {
  days: Ymd[]
  width: number
}

function HeadRow(props: HeadRowProps) {
  return (
    <div className={classes.row}>
      <div className={classes.berth}></div>
      {props.days.map((ymd) => (
        <HeadDay key={ymd} width={props.width} ymd={ymd} />
      ))}
    </div>
  )
}

function FootRow(props: { berths: Berth[] }) {
  let visibleBerths = CalendarStore.useState((s) => new Set(s.visibleBerths))
  return (
    <div className={classes.row}>
      <div style={{ width: '150px', paddingTop: '10px' }}>
        <Select
          placeholder="Find berth"
          searchable
          value={null}
          data={props.berths
            .filter(
              (b) =>
                !visibleBerths.has(b.berth_id) && b.available_days.length === 0
            )
            .map((b) => ({
              value: b.berth_id.toString(),
              label: b.name,
            }))}
          onChange={(v) => {
            console.log('test', v)
            if (v != null)
              CalendarStore.update((s) => {
                s.visibleBerths.push(parseInt(v, 10))
              })
          }}
        />
      </div>
    </div>
  )
}

interface RowProps {
  berth: Berth
  days: Ymd[]
  width: number
}

function Row(props: RowProps) {
  const { name, price, pricing, min_length, max_length } = props.berth
  const [opened, { open, close }] = useDisclosure(false)
  const isMobile = useMediaQuery('(max-width: 50em)')

  return (
    <div className={classes.row}>
      <Modal
        opened={opened}
        onClose={close}
        title={`Edit ${name}`}
        fullScreen={isMobile}
        transitionProps={{ transition: 'fade', duration: 200 }}
      >
        <EditBerthForm berth={props.berth} onCancel={close} />
      </Modal>
      <div className={classes.berth} onClick={open}>
        {name}
        <small>
          {min_length}-{max_length}m, {price}€
          {pricing === 'flat_fee' ? ' flat fee' : '/m'}
        </small>
      </div>
      {props.days.map((ymd) => (
        <DayTd key={ymd} berth={props.berth} ymd={ymd} width={props.width} />
      ))}
    </div>
  )
}

function HeadDay({ ymd, width }: { ymd: Ymd; width: number }) {
  return (
    <div
      style={{
        width: `${width}px`,
      }}
      className={classes.day}
    >
      {ymd % 100}
    </div>
  )
}

function DayTd({
  berth,
  ymd,
  width,
}: {
  berth: Berth
  ymd: Ymd
  width: number
}) {
  const queryClient = useQueryClient()
  let cls = useDayClass(berth.berth_id, ymd)
  let selectMenu = CalendarStore.useState((s) => {
    return (
      s.selectEnd === ymd &&
      berth.berth_id === s.selectedBerthId &&
      s.selectMenu
    )
  })

  let setOpened = useCallback((isOpened: boolean) => {
    if (!isOpened) CalendarStore.update(selectCanceled())
  }, [])

  let changeDays = useCallback(
    async (add: boolean) => {
      let { selectStart: start, selectEnd: end } = CalendarStore.getRawState()
      if (start == null || end == null) {
        CalendarStore.update(selectCanceled())
        return
      }
      let a = start < end ? start : end
      let b = start < end ? end : start
      let days = dayjsRange(ymdDayjs(a), ymdDayjs(b)).map((d) =>
        d.format('YYYY-MM-DD')
      )
      try {
        CalendarStore.update((s) => {
          s.saving = true
          s.visibleBerths = Array.from(
            new Set([...s.visibleBerths, berth.berth_id])
          )
        })
        let dates = days.map((date) => ({
          berth_id: berth.berth_id,
          date,
        }))
        await axios.post('/api/berthday/', {
          add_days: add ? dates : [],
          remove_days: add ? [] : dates,
        })

        queryClient.invalidateQueries({ queryKey: ['berths-by-period'] })
      } catch (e) {
        // TODO show error
      } finally {
        CalendarStore.update(selectCanceled())
      }
    },
    [berth, queryClient]
  )

  return (
    <Menu opened={selectMenu} position="bottom-end">
      <Menu.Target>
        <div
          style={{
            width: `${width}px`,
          }}
          draggable={false}
          className={classes.day + cls}
          onMouseDown={() => {
            CalendarStore.update(selectStarted(ymd, berth.berth_id))
            return false
          }}
          onMouseUp={(e) => {
            CalendarStore.update(selectEnded(ymd))
            return false
          }}
          onMouseEnter={(e) => {
            CalendarStore.update(selectOver(ymd))
          }}
        ></div>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>
          <SelectedDays />
        </Menu.Label>
        <Menu.Item
          leftSection={<IconPlus size={14} />}
          onClick={() => changeDays(true)}
        >
          Add days
        </Menu.Item>
        <Menu.Item
          leftSection={<IconMinus size={14} />}
          onClick={() => changeDays(false)}
        >
          Remove days
        </Menu.Item>
        <Menu.Item color="gray" onClick={() => setOpened(false)}>
          Cancel
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  )
}

function SelectedDays() {
  let { start, end } = CalendarStore.useState((s) => ({
    start: s.selectStart,
    end: s.selectEnd,
  }))
  if (start == null || end == null) return null
  if (start === end) {
    return <>{ymdDayjs(start).format('MMMM DD')}</>
  } else {
    let a = start < end ? start : end
    let b = start < end ? end : start
    return (
      <>
        {ymdDayjs(a).format('MMMM DD')} - {ymdDayjs(b).format('DD')}
      </>
    )
  }
}
